import React, { useContext } from 'react'

import PlannedTourCtaContent from './PlannedTourCtaContent'
import { ItemCardVariant } from '../ItemCard'
import ItineraryItemCard from '../ItineraryItemCard'

import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import OfferTileInclusions from 'components/OfferList/OfferListTiles/Support/OfferTileInclusions'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import { isLEOffer } from 'lib/offer/offerTypes'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import BookmarkLocation from 'tripPlanner/components/Bookmark/BookmarkCardNew/BookmarkLocation'
import LEOfferPriceDetails from 'tripPlanner/components/ItemOfferPrice/LEOfferPriceDetails'
import SavedOfferCard from 'tripPlanner/components/SavedOffers/SavedOfferCard'
import TripContext from 'tripPlanner/contexts/TripContext'
import useTourV1ItemConfirmationCodes from 'tripPlanner/hooks/confirmationCodes/useTourV1ItemConfirmationCodes'
import useTourV1ItemOccupancyDescription from 'tripPlanner/hooks/occupancy/useTourV1ItemOccupancyDescription'
import { TourItem } from 'tripPlanner/types/tripItem'
import {
  formatDatesAndDays,
  formatDurationDaysIfMultipleDays,
  formatStartAndEndLocation,
  isLeBookmark,
} from 'tripPlanner/utils'
import { buildProductLink } from 'tripPlanner/utils/bookmark/restoreCart'

interface Props {
  item: TourItem
  offer: App.Offer
  tripNameLabel?: React.ReactNode
  variant: ItemCardVariant
}

export default function TourV1ItemCard({
  item,
  offer,
  tripNameLabel,
  variant,
}: Props) {
  const timeFormat = useRegionTimeFormat()

  const tripContext = useContext(TripContext)
  const viewAsTemplate = tripContext?.viewAsTemplate

  let priceAreaContent: JSX.Element | null = null
  let ctaAreaContent: JSX.Element | null = null
  let timeDescription: string | undefined = formatDatesAndDays(item, timeFormat)

  const isOfferAvailable = isLEOffer(offer)
    ? scheduleIsCurrent(offer.onlinePurchaseSchedule)
    : true

  const bookingId = useTourV1ItemConfirmationCodes(item)
  const productTypeLabel = offer ? (
    <ProductTypeLabel productType={offer.productType} />
  ) : null
  const durationDescription = formatDurationDaysIfMultipleDays(item)
  const travellersDescription = useTourV1ItemOccupancyDescription(item)

  if (isLeBookmark(item)) {
    timeDescription = undefined

    const offerLink = buildProductLink(item, !viewAsTemplate, { offer }) ?? ''

    priceAreaContent = (
      <LEOfferPriceDetails
        offer={offer}
        package={offer.packages.find(
          (p) => p.id === item.savedItemData.packageId,
        )}
      />
    )
    ctaAreaContent = (
      <PlannedTourCtaContent
        item={item}
        isOfferAvailable={isOfferAvailable}
        variant={variant}
        offerLink={offerLink}
      />
    )

    const locationDescription = (
      <BookmarkLocation
        variant="single"
        startLocation={item.startPlace.name}
        showIcon={false}
      />
    )

    return (
      <SavedOfferCard
        item={item}
        name={item.name}
        imageOverlayContent={productTypeLabel}
        locationDescription={locationDescription}
        descriptors={[timeDescription, durationDescription]}
        printDescriptors={[
          timeDescription,
          durationDescription,
          travellersDescription,
        ]}
        priceAreaContent={priceAreaContent}
        ctaAreaContent={ctaAreaContent}
        isSoldOut={!isOfferAvailable}
        offerLink={offerLink}
        testId="saved-tour-card"
        tripNameLabel={tripNameLabel}
        variant={variant}
        inclusions={
          offer.tileInclusions &&
          offer.tileInclusions.length > 0 && (
            <OfferTileInclusions
              heading=""
              offerTitle={item.name}
              tileInclusionsList={offer.tileInclusions}
              maxInclusionsCount={2}
              offerURL={offerLink}
            />
          )
        }
      />
    )
  } else {
    return (
      <ItineraryItemCard
        item={item}
        name={item.name}
        imageOverlayContent={productTypeLabel}
        locationDescription={formatStartAndEndLocation(item)}
        descriptors={[timeDescription, durationDescription]}
        printDescriptors={[
          timeDescription,
          durationDescription,
          travellersDescription,
        ]}
        confirmationCode={bookingId.codeString}
        confirmationCodeLabel={bookingId.label}
        tripNameLabel={tripNameLabel}
        variant={variant}
      />
    )
  }
}
